import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Bot, User, FileText, Quote, ChevronRight, ChevronDown, Info, ChevronLeft, Menu, X } from 'lucide-react';

// Predefined questions with categories
const AVAILABLE_QUESTIONS = [
  {
    text: "What are the key factors affecting loan approval decisions?",
    category: "Loans"
  },
  {
    text: "Explain the process of onboarding a new customer in a bank.",
    category: "Customer Service"
  },
  {
    text: "What are the risks and detection methods for online transaction fraud?",
    category: "Security"
  },
  {
    text: "What is the role of KYC and AML in banking compliance?",
    category: "Compliance"
  },
  {
    text: "How do banks manage customer grievance redressal?",
    category: "Customer Service"
  }
];

// Initial knowledge base
const INITIAL_KNOWLEDGE_BASE = [
  {
    name: 'Bank_Topics.txt',
    content: [
      'Customer Onboarding Process',
      'Loan Application Workflow',
      'Interest Rate Calculation',
      'Credit Score Impact on Loan Approval',
      'Fraud Detection in Online Transactions',
      'E-Banking Services',
      'Loan Repayment Methods',
      'Bank Branch Operations',
      'Investment Banking Explained',
      'Impact of AI in Banking'
    ].join('\n')
  },
  {
    name: 'Bank_Topics_1.docx',
    content: [
      'Customer Onboarding Process',
      'Loan Application Workflow',
      'Interest Rate Calculation',
      'Credit Score Impact on Loan Approval',
      'Fraud Detection in Online Transactions',
      'Wealth Management Strategies',
      'Mutual Funds and Investments',
      'Risk Management in Banking',
      'Debt Recovery Process',
      'Customer Grievance Redressal Mechanism'
    ].join('\n')
  },
  {
    name: 'Bank_Topics_2.docx',
    content: [
      'Customer Onboarding Process',
      'Loan Application Workflow',
      'Interest Rate Calculation',
      'Credit Score Impact on Loan Approval',
      'Fraud Detection in Online Transactions',
      'Banking Regulations and Compliance',
      'KYC and AML Guidelines',
      'Treasury Management in Banks',
      'Financial Inclusion Initiatives',
      'Government Schemes for Small Businesses'
    ].join('\n')
  }
];

// QuestionSidebar Component - Now with mobile support
const QuestionSidebar = ({ onQuestionClick, selectedQuestion, isOpen, onClose }) => (
  <div className={`${isOpen ? 'flex' : 'hidden'} md:flex flex-col w-full md:w-80 border-r border-gray-200 bg-white h-full overflow-y-auto fixed md:static top-0 left-0 z-20`}>
    <div className="p-4 border-b border-gray-200 flex justify-between items-center">
      <h2 className="text-lg font-semibold text-gray-900">Available Questions</h2>
      <button 
        className="md:hidden text-gray-500 hover:text-gray-700"
        onClick={onClose}
      >
        <X size={20} />
      </button>
    </div>
    <div className="flex-1 p-4 space-y-4 overflow-y-auto">
      {Object.entries(
        AVAILABLE_QUESTIONS.reduce((acc, q) => {
          (acc[q.category] = acc[q.category] || []).push(q);
          return acc;
        }, {})
      ).map(([category, questions]) => (
        <div key={category}>
          <h3 className="text-sm font-medium text-gray-500 mb-2">{category}</h3>
          <div className="space-y-1">
            {questions.map((q, idx) => (
              <button
                key={idx}
                onClick={() => {
                  onQuestionClick(q.text);
                  if (window.innerWidth < 768) onClose(); // Close sidebar on mobile after selection
                }}
                className={`w-full text-left p-2 rounded-lg text-sm transition-colors
                  ${selectedQuestion === q.text 
                    ? 'bg-purple-100 text-purple-700' 
                    : 'text-gray-700 hover:bg-gray-50'}`}
              >
                {q.text}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Citation Component
const Citation = ({ citation }) => (
  <div className="bg-gray-50 p-3 rounded-lg mt-2 border-l-4 border-purple-500">
    <div className="flex items-start gap-2">
      <Quote className="w-4 h-4 text-purple-600 mt-1 flex-shrink-0" />
      <div className="flex-1">
        <p className="text-sm text-gray-700">{citation.text}</p>
        <div className="flex flex-wrap items-center gap-2 mt-1">
          <p className="text-xs text-gray-500">Source: {citation.source}</p>
          <span className="text-xs text-gray-400 hidden sm:inline">•</span>
          <p className="text-xs text-gray-500 font-mono">Path: /assets/{citation.source}</p>
        </div>
      </div>
    </div>
  </div>
);

// Flow Diagram Component - With mobile responsiveness
const FlowDiagram = ({ isOpen, documents, query, onDocumentClick }) => {
  if (!isOpen || !documents.length) return null;

  const getQueryRelevance = (doc) => {
    let relevance = {
      summary: '',
      path: `Path: /assets/${doc.name}`
    };

    if (query.toLowerCase().includes('loan')) {
      relevance.summary = 'This document contains:\n' +
        '• Loan application workflows\n' +
        '• Credit score requirements\n' +
        '• Approval criteria details';
    } else if (query.toLowerCase().includes('customer')) {
      relevance.summary = 'This document contains:\n' +
        '• Customer onboarding steps\n' +
        '• KYC procedures\n' +
        '• Service protocols';
    } else if (query.toLowerCase().includes('fraud')) {
      relevance.summary = 'This document contains:\n' +
        '• Fraud detection methods\n' +
        '• Security protocols\n' +
        '• Risk management procedures';
    } else {
      relevance.summary = 'This document contains relevant banking information';
    }

    return relevance;
  };

  // Mobile-friendly diagram that works on smaller screens
  return (
    <div className="p-2 md:p-4 bg-white border-t border-gray-200">
      <div className="overflow-x-auto">
        <div className="min-w-[800px] h-48 flex items-center justify-center relative">
          <svg className="w-full h-full" viewBox="0 0 1200 200">
            {/* Query Start Box */}
            <g>
              <rect
                x="50"
                y="70"
                width="150"
                height="60"
                rx="4"
                fill="#EDE9FE"
                stroke="#9333EA"
                strokeWidth="2"
              />
              <text
                x="125"
                y="100"
                textAnchor="middle"
                fill="#1e293b"
                fontSize="14"
                dy=".3em"
              >
                "{query.slice(0, 20)}..."
              </text>
            </g>

            {/* Connection to Processing Node */}
            <path
              d="M 200 100 C 250 100, 250 100, 300 100"
              stroke="#9333EA"
              strokeWidth="3"
              fill="none"
            />

            {/* Processing Node */}
            <g>
              <rect
                x="300"
                y="70"
                width="150"
                height="60"
                rx="4"
                fill="#F3E8FF"
                stroke="#9333EA"
                strokeWidth="2"
              />
              <text
                x="375"
                y="100"
                textAnchor="middle"
                fill="#1e293b"
                fontSize="14"
              >
                Query Processing
              </text>
            </g>

            {/* Connections to Documents */}
            {documents.map((_, idx) => {
              const y = 40 + (idx * 50);
              const startX = 450;
              const endX = 600;
              
              return (
                <path
                  key={`connection-${idx}`}
                  d={`M ${startX} 100 C ${(startX + endX) / 2} 100, ${(startX + endX) / 2} ${y}, ${endX} ${y}`}
                  stroke="#60a5fa"
                  strokeWidth="3"
                  fill="none"
                />
              );
            })}

            {/* Document nodes */}
            {documents.map((doc, idx) => {
              const y = 40 + (idx * 50);
              return (
                <g key={doc.name} className="document-group">
                  {/* Document box */}
                  <rect
                    x="600"
                    y={y - 15}
                    width="200"
                    height="30"
                    rx="4"
                    fill="#f3f4f6"
                    stroke="#94a3b8"
                    strokeWidth="2"
                    className="cursor-pointer hover:fill-gray-200"
                    onClick={() => onDocumentClick(doc)}
                  />
                  <text
                    x="660"
                    y={y}
                    textAnchor="middle"
                    fill="#1e293b"
                    fontSize="12"
                    dy=".3em"
                    className="cursor-pointer select-none"
                    onClick={() => onDocumentClick(doc)}
                  >
                    {doc.name.split('.')[0]}
                  </text>

                  {/* Info icon and tooltip */}
                  <g className="info-tooltip" style={{ isolation: 'isolate' }}>
                    {/* Info circle background */}
                    <circle
                      cx="770"
                      cy={y}
                      r="8"
                      fill="#9333EA"
                      className="cursor-help"
                    />
                    
                    {/* Info icon text */}
                    <text
                      x="770"
                      y={y}
                      textAnchor="middle"
                      fill="white"
                      fontSize="10"
                      dy=".3em"
                      className="select-none"
                    >
                      i
                    </text>

                    {/* Tooltip - positioned based on screen size */}
                    <foreignObject
                      x={idx % 2 === 0 ? "800" : "400"}
                      y={y - 40}
                      width="250"
                      height="100"
                      style={{ zIndex: 50 }}
                      className="tooltip-content opacity-0"
                    >
                      <div className="bg-gray-900 text-white p-2 rounded-lg text-xs shadow-lg">
                        <p className="whitespace-pre-line">{getQueryRelevance(doc).summary}</p>
                        <p className="mt-1 text-gray-300 text-xs">{getQueryRelevance(doc).path}</p>
                      </div>
                    </foreignObject>
                  </g>
                </g>
              );
            })}
          </svg>
        </div>
      </div>
      <p className="text-xs text-gray-500 text-center mt-2 md:hidden">Scroll horizontally to view full diagram</p>
    </div>
  );
};

// Chat Message Component - Responsive
const ChatMessage = ({ message, onDocumentClick }) => {
  const isBot = message.type === 'bot';
  
  return (
    <div className={`flex w-full py-4 md:py-8 ${isBot ? 'bg-white' : 'bg-gray-50'}`}>
      <div className="w-full max-w-4xl mx-auto flex px-3 md:px-4 gap-2 md:gap-4">
        <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center">
          {isBot ? (
            <div className="bg-purple-100 w-full h-full rounded-full flex items-center justify-center">
              <Bot className="w-5 h-5 text-purple-600" />
            </div>
          ) : (
            <div className="bg-blue-100 w-full h-full rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-blue-600" />
            </div>
          )}
        </div>
        
        <div className="flex-1">
          <p className="text-gray-800 whitespace-pre-wrap text-sm md:text-base">{message.content}</p>
          
          {/* Combined Citations and Document References */}
          {isBot && message.citations && message.citations.length > 0 && (
            <div className="mt-3 md:mt-4 space-y-2">
              <p className="text-xs md:text-sm text-gray-600 font-medium">Supporting Evidence:</p>
              {message.citations.map((citation, idx) => (
                <Citation key={idx} citation={citation} />
              ))}
            </div>
          )}
          
          {/* Hidden document references - used only for flow diagram */}
          <div className="hidden">
            {isBot && message.documents && message.documents.length > 0 && (
              <div className="mt-4">
                {message.documents.map((doc, idx) => (
                  <button
                    key={idx}
                    onClick={() => onDocumentClick(doc)}
                    className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
                  >
                    <FileText className="w-4 h-4" />
                    <span>{doc.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main KnowledgeChat Component
const KnowledgeChat = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [showFlowDiagram, setShowFlowDiagram] = useState(false);
  const [knowledgeBase, setKnowledgeBase] = useState(INITIAL_KNOWLEDGE_BASE);
  const [currentDocuments, setCurrentDocuments] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const messagesEndRef = useRef(null);

  const handleLogout = () => {
    sessionStorage.removeItem('isAuthenticated');
    navigate('/signin');
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Close sidebar when screen resizes to desktop view
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const findRelevantDocuments = (userQuery) => {
    const queryTerms = userQuery.toLowerCase().split(' ');
    return knowledgeBase.filter(doc => 
      queryTerms.some(term => doc.content.toLowerCase().includes(term))
    );
  };

  const findCitations = (query, docs) => {
    const queryTerms = query.toLowerCase().split(' ');
    let citations = [];

    docs.forEach(doc => {
      const lines = doc.content.split('\n');
      lines.forEach(line => {
        const trimmedLine = line.trim();
        if (trimmedLine && queryTerms.some(term => line.toLowerCase().includes(term))) {
          citations.push({
            text: trimmedLine,
            source: doc.name
          });
        }
      });
    });

    return citations.filter((citation, index, self) =>
      index === self.findIndex((c) => c.text === citation.text)
    );
  };

  const generateResponse = (userQuery, relevantDocs) => {
    let response = '';
    
    if (userQuery.toLowerCase().includes('loan approval')) {
      response = "Based on the analyzed documents, here are the key factors affecting loan approval decisions:\n\n" +
                "1. Credit Score Evaluation - Primary factor in determining creditworthiness\n" +
                "2. Loan Application Workflow - Standardized process for assessment\n" +
                "3. Banking Compliance Requirements - Regulatory guidelines\n" +
                "4. Risk Assessment Criteria - Comprehensive evaluation";
    } 
    else if (userQuery.toLowerCase().includes('customer onboarding')) {
      response = "The customer onboarding process involves these key steps:\n\n" +
                "1. Initial Documentation - Collection of required papers\n" +
                "2. KYC Verification - Identity and background checks\n" +
                "3. Risk Assessment - Customer profile evaluation\n" +
                "4. Account Setup - Final activation";
    }
    else if (userQuery.toLowerCase().includes('fraud') || userQuery.toLowerCase().includes('online transaction')) {
      response = "Regarding fraud detection and online transaction security:\n\n" +
                "1. Real-time Monitoring - Continuous transaction surveillance\n" +
                "2. Security Protocols - Multi-layer authentication\n" +
                "3. Risk Management - Fraud prevention measures";
    }
    else {
      response = "Based on the analyzed documents, here's what I found:";
    }

    // Create citations that include both document summaries and content matches
    const citations = [];
    
    relevantDocs.forEach(doc => {
      // Add document summary as a citation
      let summaryText = "";
      if (doc.name.includes('Bank_Topics.txt')) {
        summaryText = userQuery.toLowerCase().includes('loan') 
          ? "Contains detailed information about loan application workflow and credit score impact."
          : userQuery.toLowerCase().includes('customer')
          ? "Outlines the complete customer onboarding process and service details."
          : "Covers core banking processes and security measures.";
      } else if (doc.name.includes('Bank_Topics_1.docx')) {
        summaryText = userQuery.toLowerCase().includes('loan')
          ? "Details risk management procedures and debt recovery processes."
          : userQuery.toLowerCase().includes('customer')
          ? "Explains customer service protocols and grievance handling mechanisms."
          : "Contains information about banking operations and security.";
      } else if (doc.name.includes('Bank_Topics_2.docx')) {
        summaryText = userQuery.toLowerCase().includes('loan')
          ? "Describes banking regulations and compliance requirements for loans."
          : userQuery.toLowerCase().includes('customer')
          ? "Covers KYC guidelines and regulatory compliance procedures."
          : "Details banking policies and regulatory frameworks.";
      }
      
      citations.push({
        text: summaryText,
        source: doc.name
      });

      // Add relevant content matches
      const lines = doc.content.split('\n');
      lines.forEach(line => {
        const trimmedLine = line.trim();
        if (trimmedLine && 
            userQuery.toLowerCase().split(' ').some(term => 
              line.toLowerCase().includes(term)) &&
            !citations.some(c => c.text === trimmedLine)) {
          citations.push({
            text: trimmedLine,
            source: doc.name
          });
        }
      });
    });

    return {
      content: response,
      citations: citations,
      documents: relevantDocs
    };
  };

  const isQuerySupported = (userQuery) => {
    const queryLower = userQuery.toLowerCase();
    return AVAILABLE_QUESTIONS.some(q => 
      queryLower.includes(q.text.toLowerCase()) ||
      q.text.toLowerCase().includes(queryLower)
    );
  };

  const processQuery = async (userQuery) => {
    setIsTyping(true);
    
    if (!isQuerySupported(userQuery)) {
      const newMessages = [
        ...messages,
        { type: 'user', content: userQuery },
        {
          type: 'bot',
          content: "I apologize, but I don't have enough information in my knowledge base to answer this question accurately. Please try one of the available questions listed on the left, which I can answer based on my current documentation.",
          citations: [],
          documents: []
        }
      ];
      setMessages(newMessages);
      setIsTyping(false);
      return;
    }

    await new Promise(resolve => setTimeout(resolve, 1000));
    const relevantDocs = findRelevantDocuments(userQuery);
    const response = generateResponse(userQuery, relevantDocs);
    setCurrentDocuments(relevantDocs);

    const newMessages = [
      ...messages,
      { type: 'user', content: userQuery },
      {
        type: 'bot',
        content: response.content,
        citations: response.citations,
        documents: relevantDocs
      }
    ];

    setMessages(newMessages);
    setIsTyping(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      processQuery(query);
      setQuery('');
    }
  };

  const handleQuestionClick = (questionText) => {
    setSelectedQuestion(questionText);
    setQuery(questionText);
    handleSubmit({ preventDefault: () => {}, target: {} });
  };

  const openDocument = (doc) => {
    // Use the actual file path
    const filePath = `/assets/${doc.name}`;
    window.open(filePath, '_blank');
  };

  // Overlay for when mobile sidebar is open
  const Overlay = ({ isOpen, onClose }) => (
    isOpen ? (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-10 md:hidden"
        onClick={onClose}
      />
    ) : null
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar Overlay */}
      <Overlay isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-full mx-auto px-3 md:px-4 py-2 md:py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-4">
            {/* Mobile menu button */}
            <button
              className="md:hidden p-1 text-gray-700"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu size={24} />
            </button>
            
            <button
              onClick={() => navigate('/')}
              className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <ChevronLeft size={18} />
              <span className="text-sm">Back</span>
            </button>
            
            <div className="flex items-center gap-2">
              <Bot className="w-6 h-6 md:w-8 md:h-8 text-purple-600" />
              <h1 className="text-base md:text-xl font-semibold text-gray-900 truncate">
                <span className="hidden sm:inline">Banking Knowledge Assistant</span>
                <span className="sm:hidden">KB Assistant</span>
              </h1>
            </div>
          </div>
          
          <div className="flex items-center gap-2 md:gap-4">
            <div className="text-xs md:text-sm text-gray-500 hidden sm:block">
              Powered by AI & Document Intelligence
            </div>
            <button
              onClick={handleLogout}
              className="px-2 py-1 md:px-4 md:py-2 text-xs md:text-sm text-red-600 hover:text-red-800 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex min-h-[calc(100vh-57px)] md:min-h-[calc(100vh-64px)]">
        {/* Sidebar - Hidden on mobile until toggled */}
        <QuestionSidebar 
          onQuestionClick={handleQuestionClick}
          selectedQuestion={selectedQuestion}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col max-h-[calc(100vh-57px)] md:max-h-[calc(100vh-64px)]">
          {/* Messages Section */}
          <div className="flex-1 overflow-y-auto bg-gray-50">
            {messages.length === 0 && (
              <div className="w-full py-8 text-center text-gray-500 px-4">
                <p className="mb-4">Select a question or ask your own!</p>
                
                {/* Quick question buttons for mobile */}
                <div className="md:hidden space-y-2">
                  {AVAILABLE_QUESTIONS.slice(0, 3).map((q, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleQuestionClick(q.text)}
                      className="w-full text-left p-3 rounded-lg text-sm bg-white border border-gray-200 shadow-sm"
                    >
                      {q.text}
                    </button>
                  ))}
                  <button
                    onClick={() => setSidebarOpen(true)}
                    className="w-full text-center p-2 text-purple-600 text-sm"
                  >
                    See all questions
                  </button>
                </div>
              </div>
            )}
            
            {messages.map((msg, idx) => (
              <ChatMessage
                key={idx}
                message={msg}
                onDocumentClick={openDocument}
              />
            ))}
            
            {isTyping && (
              <div className="w-full py-4 md:py-8 bg-white">
                <div className="max-w-4xl mx-auto px-3 md:px-4 flex gap-2 md:gap-4">
                  <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                    <Bot className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <div className="animate-pulse flex space-x-2">
                      <div className="h-2 w-2 bg-gray-300 rounded-full" />
                      <div className="h-2 w-2 bg-gray-300 rounded-full" />
                      <div className="h-2 w-2 bg-gray-300 rounded-full" />
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Flow Diagram Section */}
          {currentDocuments.length > 0 && (
            <div className="border-t border-gray-200">
              <button
                onClick={() => setShowFlowDiagram(!showFlowDiagram)}
                className="w-full px-3 md:px-4 py-2 flex items-center justify-between text-sm text-gray-700 hover:bg-gray-50"
              >
                <div className="flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  <span>Document Connection Flow</span>
                </div>
                {showFlowDiagram ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              <FlowDiagram
                isOpen={showFlowDiagram}
                documents={currentDocuments}
                query={query}
                onDocumentClick={openDocument}
              />
            </div>
          )}

          {/* Input Form */}
          <div className="border-t border-gray-200 bg-white p-2 md:p-4">
            <form onSubmit={handleSubmit} className="flex gap-2">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ask a question..."
                className="flex-1 p-2 md:p-3 text-sm md:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <button
                type="submit"
                disabled={isTyping}
                className="px-3 md:px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <Search className="w-5 h-5" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeChat;